import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BookOpen, GraduationCap, Sparkles } from "lucide-react";
import type { CurriculumAssignment, CourseAssignment } from "@/hooks/useAssignmentsAdmin";

interface NewAssignmentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  curricula: CurriculumAssignment[];
  courses: CourseAssignment[];
  onStartLearning: () => void;
  onLater: () => void;
}

export function NewAssignmentsDialog({
  open,
  onOpenChange,
  curricula,
  courses,
  onStartLearning,
  onLater,
}: NewAssignmentsDialogProps) {
  const navigate = useNavigate();
  const totalAssignments = curricula.length + courses.length;

  const handleStartLearning = () => {
    onStartLearning();
    
    // Navigate to the first assignment
    if (curricula.length > 0) {
      navigate(`/curricula/${curricula[0].curriculum_id}`);
    } else if (courses.length > 0) {
      navigate(`/courses/${courses[0].course_id}`);
    } else {
      navigate("/curricula");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-xl">
            New Learning Assignment{totalAssignments > 1 ? "s" : ""}!
          </DialogTitle>
          <DialogDescription>
            You have been assigned {totalAssignments} new{" "}
            {totalAssignments > 1 ? "items" : "item"} to learn. Would you like
            to start now?
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[200px] pr-4">
          <div className="space-y-3">
            {curricula.map((assignment) => (
              <div
                key={assignment.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                  <GraduationCap className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-sm">
                    {assignment.curricula?.title || "Curriculum"}
                  </p>
                  <p className="text-xs text-muted-foreground">Curriculum</p>
                </div>
              </div>
            ))}

            {courses.map((assignment) => (
              <div
                key={assignment.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/10">
                  <BookOpen className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="font-medium text-sm">
                    {assignment.courses?.title || "Course"}
                  </p>
                  <p className="text-xs text-muted-foreground">Course</p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button onClick={handleStartLearning} className="w-full">
            Start Learning Now
          </Button>
          <Button variant="outline" onClick={onLater} className="w-full">
            Maybe Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
